// WAAP Analytics → Threat Investigation — NATIVE charts (no Superset iframe).
// The BE pre-normalizes Superset dashboard 42 ("Threat Investigation") into
// typed widgets: the Access Log trend + the raw access-log Details table.
//   GET /analytics/threat-investigation/charts?tz=…&range=…&domains=…&filters=…
//   GET /analytics/threat-investigation/filter-options?tz=…&range=…&domains=…
import { useQuery } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { waapKeys, waapPath, WAAP_AREAS } from './_shared';
import type { WaapChartsResponse } from './dashboard-charts';

/** Time-range options for Threat Investigation — Superset NATIVE relative ranges
 *  (parsed server-side into from/to), so the exact strings matter. */
export const THREAT_TIME_RANGES = [
  'Last 1 hour',
  'Last 4 hours',
  'Last 12 hours',
  'Last 24 hours',
  'Last 7 days',
  'Last 30 days',
] as const;
export type ThreatTimeRange = (typeof THREAT_TIME_RANGES)[number];

/** Sidebar filter values: each access-log column maps to a list of selected
 *  values; the response-time bound is `upstream_response_time_min`. */
export type WaapThreatFilters = Record<string, string | string[]>;

/** Distinct dropdown options for the sidebar filters (keyed by access-log
 *  column: src_ip / path / status / … ). */
export type WaapThreatFilterOptions = Record<string, string[]>;

/** Drop empty/blank filter entries so the query key + payload stay stable. */
function cleanFilters(filters: WaapThreatFilters): WaapThreatFilters {
  const out: WaapThreatFilters = {};
  for (const [k, v] of Object.entries(filters)) {
    if (Array.isArray(v)) {
      const arr = v.filter((x) => x.trim() !== '');
      if (arr.length) out[k] = arr;
    } else if (v.trim() !== '') {
      out[k] = v.trim();
    }
  }
  return out;
}

/** Read the distinct option lists for the sidebar dropdowns. Returns {} when the
 *  board is not configured (the tab still renders with empty option lists). */
export function useWaapThreatFilterOptions(
  domainId: string | undefined,
  range = 'Last 4 hours',
  domains: string[] = [],
) {
  const domainsKey = [...domains].sort().join(',');
  return useQuery({
    queryKey: [
      ...waapKeys.aspect(domainId ?? '', WAAP_AREAS.threatInvestigationFilterOptions),
      range,
      domainsKey,
    ] as const,
    enabled: !!domainId,
    retry: false,
    staleTime: 60_000,
    queryFn: async (): Promise<WaapThreatFilterOptions> => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const params = new URLSearchParams({ tz, range });
        if (domainsKey) params.set('domains', domainsKey);
        const path = waapPath(domainId as string, WAAP_AREAS.threatInvestigationFilterOptions);
        const res = await serviceApiV3.get<WaapThreatFilterOptions>(`${path}?${params.toString()}`);
        return res.data ?? {};
      } catch {
        return {};
      }
    },
  });
}

/** Read the normalized Threat Investigation widgets for a website's account.
 *  `domains` + `filters` narrow both the Access Log trend and the Details log.
 *  Returns null when the board is not configured server-side (400/404/501). */
export function useWaapThreatCharts(
  domainId: string | undefined,
  range = 'Last 4 hours',
  domains: string[] = [],
  filters: WaapThreatFilters = {},
) {
  const domainsKey = [...domains].sort().join(',');
  const filtersKey = JSON.stringify(cleanFilters(filters));
  return useQuery({
    queryKey: [
      ...waapKeys.aspect(domainId ?? '', WAAP_AREAS.threatInvestigationCharts),
      range,
      domainsKey,
      filtersKey,
    ] as const,
    enabled: !!domainId,
    retry: false,
    staleTime: 60_000,
    queryFn: async (): Promise<WaapChartsResponse | null> => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const params = new URLSearchParams({ tz, range });
        if (domainsKey) params.set('domains', domainsKey);
        if (filtersKey !== '{}') params.set('filters', filtersKey);
        const path = waapPath(domainId as string, WAAP_AREAS.threatInvestigationCharts);
        const res = await serviceApiV3.get<WaapChartsResponse>(`${path}?${params.toString()}`);
        return res.data;
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404 || status === 501) return null;
        throw e;
      }
    },
  });
}
